import { useState, useCallback } from 'react';

interface UseFileDragDropProps {
  onFilesAdded: (files: FileList) => void;
  disabled?: boolean;
}

interface UseFileDragDropReturn {
  isDragActive: boolean;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
}

/**
 * Custom hook for handling drag and drop file operations
 * @param onFilesAdded - Callback function when files are dropped
 * @param disabled - Whether drag and drop should be disabled
 * @returns Drag and drop state and handlers
 */
export const useFileDragDrop = ({
  onFilesAdded,
  disabled = false,
}: UseFileDragDropProps): UseFileDragDropReturn => {
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragActive(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFilesAdded(files);
      }
    },
    [onFilesAdded, disabled]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragActive(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback(() => {
    setIsDragActive(false);
  }, []);

  return {
    isDragActive,
    handleDrop,
    handleDragOver,
    handleDragLeave,
  };
};
